import React, { useState, useEffect, useMemo, useRef } from 'react';
import BRAND from '../brand';
import FilterChips from '../components/FilterChips';
import { EmptyStateIcon, SectionLoader } from '../components/NavIcons';
import ProductCard from '../components/ProductCard';
import SubscriptionCatalog from '../components/SubscriptionCatalog';
import BouquetCatalog from '../components/BouquetCatalog';
import { firstPlannedAvailable } from '../utils/bouquetAvailability';
import CertificatePurchasePage from '../components/CertificatePurchasePage';
import UnavailableNotice from '../components/UnavailableNotice';
import { api, getImageUrl } from '../api';
import { getPlatform } from '../platform';
import { setIdlePrefetch } from '../utils/imageQueue';
import { getDeepLinkTarget, takeDeepLink, replaceCategoryUrl, leaveLandingUrl, finishDeepLink } from '../utils/deepLink';
import { isProductOrderable, pickSimilarProducts, loadItemInfo, displayName } from '../utils/unavailable';
import { COLLECTIONS_STUB, loadCollection, loadOtherCollectionsItems } from '../utils/collections';
import { trackGoal } from '../utils/metrika';

// Заголовок раздела в каталоге (и подборки — тем же видом)
const SECTION_TITLE_STYLE = {
  fontSize: '20px',
  fontWeight: '600',
  marginBottom: '16px',
  // без своего отступа: левый край — как у карточек и чипов (владелец 29.10)
  color: '#000',
  // см. BouquetCatalog: высота полосы категорий меряется, а не константа
  scrollMarginTop: 'calc(var(--header-height, 120px) + var(--chips-height, 56px) + 8px)'
};

function MenuPage({ addToCart, addBouquetToCart, cart, updateQuantity, updateBouquetQuantity, catalogMode = 'products', onCatalogModeChange, onAddSubscriptionToCart, subscriptionCart, onSubSelectionChange, goToOrders, onLoadingChange }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bouquetCatalog, setBouquetCatalog] = useState([]);  // для расчёта пересечений с букетами в корзине
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [maxDisplayQuantity, setMaxDisplayQuantity] = useState(null);
  const [showCertBanner, setShowCertBanner] = useState(false);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const categorySectionRefs = useRef({});
  const platform = getPlatform();
  // Блок «Сейчас нет в продаже» / «Такой страницы нет» наверху каталога (ТЗ «реклама по
  // наличию», В1–В3): { id, status, note, name, image, category, similar: [товары], loading }
  const [notice, setNotice] = useState(null);
  const noticeRef = useRef(null);
  // Раздел выбирает сам блок — к заголовку раздела не прокручиваем, на экране блок
  const skipCategoryScrollRef = useRef(false);
  // Подборка /podborka/<slug>/: { found, title, items } — пока null, грузится
  const [collection, setCollection] = useState(null);
  const [collectionLeft, setCollectionLeft] = useState(false);
  const deepTarget = getDeepLinkTarget();
  const collectionSlug = deepTarget && deepTarget.kind === 'collection' && !collectionLeft ? deepTarget.id : null;

  // Сообщаем App о загрузке каталога — плавающая кнопка корзины
  // не должна висеть поверх сплэша
  useEffect(() => {
    if (onLoadingChange) onLoadingChange(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Сколько каждого product_id занято корзиной клиента (прямые стебли + компоненты букетов).
  // Передаём в каждую ProductCard, чтобы лимит +/- учитывал «забронированное» букетами.
  const cartStemUsage = useMemo(() => {
    const usage = {};
    (cart || []).forEach(item => {
      if (item.kind === 'bouquet') {
        const b = bouquetCatalog.find(x => x.id === (item.bouquet_id || item.id));
        if (!b?.components) return;
        b.components.forEach(c => {
          usage[c.product_id] = (usage[c.product_id] || 0) + c.quantity * item.quantity;
        });
      } else {
        const pid = item.product_id || item.id;
        usage[pid] = (usage[pid] || 0) + item.quantity;
      }
    });
    return usage;
  }, [cart, bouquetCatalog]);

  useEffect(() => {
    loadData();
  }, []);

  // Реконсилер корзины поднят в App.jsx — здесь больше не дублируем.

  // Скролл к категории при изменении activeCategory
  useEffect(() => {
    if (skipCategoryScrollRef.current) {
      skipCategoryScrollRef.current = false;
      return;
    }
    if (activeCategory) {
      const element = categorySectionRefs.current[activeCategory];
      console.log('useEffect scroll - activeCategory:', activeCategory);
      console.log('useEffect scroll - element:', element);

      if (element) {
        // scrollIntoView с учетом scroll-margin-top на элементе
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [activeCategory]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Загружаем категории
      const categoriesData = await api.getCategories();
      setCategories(categoriesData);

      // Загружаем ВСЕ товары сразу (максимум 200 - ограничение API)
      const response = await api.getProducts(null, null, 1, 200);
      const allProducts = response.items || response;
      setProducts(allProducts);
      if (response.max_display_quantity) setMaxDisplayQuantity(response.max_display_quantity);

      // Каталог букетов (для расчёта cartStemUsage — нужны компоненты каждого букета)
      try {
        const bResp = await api.getBouquets({ limit: 200 });
        setBouquetCatalog(bResp.items || []);
      } catch (e) { /* ignore */ }

      try {
        const cs = await api.getCertificateSettings();
        setShowCertBanner(!!cs.show_banner);
      } catch (e) {
        // ignore
      }
    } catch (error) {
      console.log('API not available:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Пришли по ссылке на раздел (/catalog/… или c_<id> из бота): раздел стеблей
  // выбираем здесь, раздел букетов — переключаем витрину, его выберет BouquetCatalog
  useEffect(() => {
    if (isLoading) return;
    const t = getDeepLinkTarget();
    if (!t || t.kind !== 'category') return;
    const cat = categories.find(c => c.id === t.id);
    if (!cat) return;
    if (cat.scope === 'bouquets') {
      if (catalogMode !== 'bouquets' && onCatalogModeChange) onCatalogModeChange('bouquets');
      return;
    }
    if (takeDeepLink('category', cat.id)) setActiveCategory(cat.slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const openNotice = ({ missing = false, name = null, image = null, category = null, excludeId = null, flower = null }) => {
    trackGoal('item_unavailable', { kind: missing ? 'missing' : 'product', item_id: excludeId });
    setNotice({
      id: Date.now(),
      status: missing ? 'Такой страницы нет' : 'Сейчас нет в продаже',
      note: missing ? 'Ссылка устарела или в ней опечатка' : null,
      name: missing ? null : displayName(name) || null,
      image: missing ? null : image,
      category: missing ? null : category,
      similar: pickSimilarProducts(products, missing ? {} : {
        name, flower, categoryId: category ? category.id : null, excludeId,
      }),
      loading: false,
    });
  };

  // Пришли по ссылке на товар, которого нет в списке витрины (скрыт, продан, в архиве) или
  // купить нечего, — блок «Сейчас нет в продаже» с похожими. Неверный адрес — «Такой страницы нет».
  // Товар в продаже открывает своё окно сам (ProductCard).
  useEffect(() => {
    if (isLoading) return undefined;
    const t = getDeepLinkTarget();
    if (!t) return undefined;
    if (t.kind === 'missing') {
      if (takeDeepLink('missing', t.id)) openNotice({ missing: true });
      return undefined;
    }
    if (t.kind === 'category') {
      // Раздела из ссылки (c_<id> из бота) больше нет (список разделов при этом загрузился)
      if (categories.length > 0 && !categories.some(c => c.id === t.id) && takeDeepLink('category', t.id)) {
        openNotice({ missing: true });
      }
      return undefined;
    }
    if (t.kind !== 'product') return undefined;
    const known = products.find(p => p.id === t.id);
    if (known && isProductOrderable(known)) return undefined;
    if (!takeDeepLink('product', t.id)) return undefined;
    if (known) {
      const img = known.images && known.images[0];
      openNotice({
        name: known.name, category: known.category, excludeId: known.id, flower: known.flower,
        image: img && img.image_url ? getImageUrl(img.image_medium || img.image_url) : null,
      });
      return undefined;
    }
    let alive = true;
    loadItemInfo('product', t.id).then(info => {
      if (!alive) return;
      if (info.state === 'missing') openNotice({ missing: true });
      else openNotice({ name: info.name, image: info.image, category: info.category, excludeId: t.id, flower: info.flower });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Подборка: состав с сервера (заглушке нужен список витрины — ждёт его). Один раз: повторная
  // установка того же ответа перезапускала бы блок пустой подборки ниже
  useEffect(() => {
    if (!collectionSlug || collection || (COLLECTIONS_STUB && isLoading)) return undefined;
    let alive = true;
    loadCollection(collectionSlug, products).then(c => { if (alive) setCollection(c); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionSlug, isLoading]);

  // Пустая или неизвестная подборка — блок «Сейчас нет в продаже» с похожими из других подборок
  useEffect(() => {
    if (!collectionSlug || isLoading || !collection) return undefined;
    if (collection.found && collection.items.length > 0) return undefined;
    if (!takeDeepLink('collection', collectionSlug)) return undefined;
    trackGoal('item_unavailable', { kind: 'collection', slug: collectionSlug });
    setNotice({
      id: Date.now(),
      status: collection.found ? 'Сейчас нет в продаже' : 'Такой страницы нет',
      note: collection.found ? null : 'Ссылка устарела или в ней опечатка',
      name: collection.found ? collection.title : null,
      image: null, category: null, similar: [], loading: true,
    });
    // Без отмены по смене зависимостей: блок показывается один раз (takeDeepLink), а ответ
    // приходит в уже показанный блок — иначе он остался бы с вечной загрузкой
    loadOtherCollectionsItems(collectionSlug, products).then(items => {
      // Карточки — из списка витрины, когда товар там есть (те же объекты, что в каталоге)
      const similar = items.map(it => products.find(p => p.id === it.id) || it);
      setNotice(s => s && { ...s, loading: false, similar: similar.length ? similar : pickSimilarProducts(products) });
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionSlug, isLoading, collection]);

  const handleCategoryChange = (categorySlug) => {
    console.log('handleCategoryChange:', categorySlug, 'current:', activeCategory);
    // Переключаем активную категорию (если кликнули по той же - снимаем выбор)
    const next = activeCategory === categorySlug ? null : categorySlug;
    setActiveCategory(next);
    // На сайте адрес раздела — в строке браузера (им можно поделиться)
    replaceCategoryUrl(next ? categories.find(c => c.slug === next) : null);
  };

  const getCartItem = (productId) => {
    return cart.find(item => item.id === productId);
  };

  // Группируем товары по категориям
  const groupProductsByCategories = () => {
    const groups = {};

    // Создаем группы для каждой категории
    categories.forEach(category => {
      groups[category.slug] = {
        category: category,
        products: []
      };
    });

    // Распределяем товары по группам
    products.forEach(product => {
      if (product.category && product.category.slug) {
        const categorySlug = product.category.slug;
        if (groups[categorySlug]) {
          groups[categorySlug].products.push(product);
        }
      }
    });

    // Сортировка внутри каждой категории:
    // 1. Товары с текущим остатком (quantity > 0) — по убыванию количества
    // 2. Товары только с плановыми поставками — по ближайшей дате
    // 3. Остальные
    Object.values(groups).forEach(group => {
      group.products.sort((a, b) => {
        const aQty = a.quantity || 0;
        const bQty = b.quantity || 0;
        const aStep = a.step || 1;
        const bStep = b.step || 1;
        const aInStock = aQty >= aStep;
        const bInStock = bQty >= bStep;

        if (aInStock && bInStock) return bQty - aQty;
        if (aInStock) return -1;
        if (bInStock) return 1;

        const aDate = a.future_stock?.[0]?.date || '9999-99-99';
        const bDate = b.future_stock?.[0]?.date || '9999-99-99';
        if (aDate !== bDate) return aDate.localeCompare(bDate);
        const aFuture = firstPlannedAvailable(a.future_stock);
        const bFuture = firstPlannedAvailable(b.future_stock);
        return bFuture - aFuture;
      });
    });

    // Фильтруем группы, оставляем только те, где есть товары
    return Object.keys(groups)
      .filter(slug => groups[slug].products.length > 0)
      .map(slug => groups[slug]);
  };

  const productGroups = groupProductsByCategories();

  // Подборка с товарами — показываем её вместо каталога; карточки — из списка витрины, когда есть
  const collectionItems = collectionSlug && collection && collection.found
    ? collection.items.map(it => products.find(p => p.id === it.id) || it)
    : [];
  const collectionPending = !!collectionSlug && (isLoading || !collection);
  const showCollection = !collectionPending && collectionItems.length > 0;

  const leaveCollection = () => {
    finishDeepLink();
    setCollectionLeft(true);
    leaveLandingUrl(null);
    try { window.scrollTo(0, 0); } catch (e) { /* jsdom */ }
  };

  // Когда фото открытой вкладки догружены — первые фото первого экрана соседней
  // (стебли ↔ букеты), чтобы при переходе они были сразу (utils/imageQueue.js)
  const firstPhoto = item => {
    const img = item.images && item.images[0];
    return img && img.image_url ? getImageUrl(img.image_medium || img.image_url) : null;
  };
  const neighborFirstScreen = isLoading || catalogMode === 'subscription' ? []
    : catalogMode === 'bouquets'
      ? productGroups.flatMap(g => g.products).slice(0, 6).map(firstPhoto).filter(Boolean)
      : bouquetCatalog.slice(0, 6).map(firstPhoto).filter(Boolean);
  const neighborKey = neighborFirstScreen.join('|');
  useEffect(() => {
    setIdlePrefetch(neighborKey ? neighborKey.split('|') : []);
    return () => setIdlePrefetch([]);
  }, [neighborKey]);

  // Используем только категории товаров (из productGroups) для фильтров
  const categoriesWithProducts = productGroups.map(group => group.category);

  // Раздел позиции из блока — если он есть в каталоге: блок стоит прямо над ним
  const noticeCategory = notice && notice.category
    ? categoriesWithProducts.find(c => c.id === notice.category.id) || null
    : null;

  // Блок показан: адрес — раздела (или главной, без записи истории), раздел выбран, блок на экране
  const noticeId = notice ? notice.id : null;
  useEffect(() => {
    if (!noticeId) return;
    if (collectionSlug) leaveCollection();
    else leaveLandingUrl(noticeCategory);
    if (noticeCategory && noticeCategory.slug !== activeCategory) {
      skipCategoryScrollRef.current = true;
      setActiveCategory(noticeCategory.slug);
    }
    try {
      if (noticeCategory && noticeRef.current) noticeRef.current.scrollIntoView({ block: 'start' });
      else window.scrollTo(0, 0);
    } catch (e) { /* jsdom */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  const closeNotice = () => {
    trackGoal('item_unavailable_close', {});
    setNotice(null);
  };

  // inNotice — карточка в ленте блока: компактная, «положил из блока» — отдельная цель Метрики
  const renderProductCard = (product, inNotice = false) => (
    <ProductCard
      key={product.id}
      product={product}
      addToCart={inNotice ? (p) => { trackGoal('item_unavailable_add', { item_id: p.id }); addToCart(p); } : addToCart}
      cartItem={getCartItem(product.id)}
      updateQuantity={updateQuantity}
      maxDisplayQuantity={maxDisplayQuantity}
      cartStemUsage={cartStemUsage[product.id] || 0}
      compact={inNotice}
    />
  );
  const noticeEl = notice && (
    <UnavailableNotice
      status={notice.status}
      note={notice.note}
      name={notice.name}
      image={notice.image}
      loading={notice.loading}
      onClose={closeNotice}
      noticeRef={noticeRef}
    >
      {notice.similar.map(p => renderProductCard(p, true))}
    </UnavailableNotice>
  );
  const noticeAtTop = !noticeCategory;

  // Full-page оформление покупки сертификата (вместо модалки)
  if (certModalOpen) {
    return (
      <CertificatePurchasePage onBack={() => setCertModalOpen(false)} goToOrders={goToOrders} />
    );
  }

  return (
    <>
      {catalogMode === 'subscription' ? (
        <SubscriptionCatalog onAddToCart={onAddSubscriptionToCart} subscriptionCart={subscriptionCart} onSelectionChange={onSubSelectionChange} />
      ) : catalogMode === 'bouquets' ? (
        <BouquetCatalog
          cart={cart}
          addBouquetToCart={addBouquetToCart}
          updateBouquetQuantity={updateBouquetQuantity}
        />
      ) : collectionPending ? (
        <div className="products-container"><SectionLoader /></div>
      ) : showCollection ? (
        // Подборка по цветку (/podborka/<slug>/): только её товары, обычными карточками
        <div className="products-container">
          <h2 style={SECTION_TITLE_STYLE}>{collection.title}</h2>
          <div className="products-grid">{collectionItems.map(p => renderProductCard(p))}</div>
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <button className="primary-btn" onClick={leaveCollection}>В каталог</button>
          </div>
        </div>
      ) : (
      <>
      {/* Пока идёт общая загрузка — только сплэш: чипы категорий и баннер
          не должны выпрыгивать раньше каталога и сдвигать надпись */}
      {!isLoading && (
      <FilterChips
        categories={categoriesWithProducts}
        activeCategory={activeCategory}
        setActiveCategory={handleCategoryChange}
      />
      )}
      {!isLoading && showCertBanner && platform !== 'web' && (
        <div
          onClick={() => setCertModalOpen(true)}
          style={{
            // Цвета — только токены бренда (раньше стояла чужая оранжевая палитра,
            // одна на все инстансы). Бордер нужен брендам, у которых фон страницы
            // совпадает с началом градиента подложки (Букетберри: кремовый).
            margin: '12px 16px',
            padding: '14px 16px',
            background: 'var(--primary-soft-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {/* Значок на фирменной подложке: акцент Стемса (светло-жёлтый) сам по себе
              на светлом фоне не читается — бейдж + on-primary работает у всех брендов */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 12,
              background: 'var(--primary-color)',
              color: 'var(--on-primary)',
              flexShrink: 0,
            }}
          >
            <EmptyStateIcon name="gift" size={22} color="currentColor" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-color)' }}>{BRAND.certBanner.title}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{BRAND.certBanner.subtitle}</div>
          </div>
          <div style={{ fontSize: 22, color: 'var(--text-secondary)' }}>›</div>
        </div>
      )}
      <div className="products-container">
        {isLoading ? (
          <SectionLoader />
        ) : products.length === 0 ? (
          <>
            {noticeEl}
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              Товары не найдены
            </div>
          </>
        ) : (
          <>
            {noticeAtTop && noticeEl}
            {productGroups.map((group) => (
              <div
                key={group.category.slug}
                style={{ marginBottom: '32px' }}
              >
                {!noticeAtTop && noticeCategory.id === group.category.id && noticeEl}
                <h2
                  ref={el => {
                    if (el) {
                      categorySectionRefs.current[group.category.slug] = el;
                    }
                  }}
                  style={SECTION_TITLE_STYLE}
                >
                  {group.category.name}
                </h2>
                <div className="products-grid">
                  {group.products.map(p => renderProductCard(p))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      </>
      )}
    </>
  );
}

export default MenuPage;
